'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { socials } from '@/types/socials';
import { Sites } from '@/types/enums';

const QUICK_LINKS = [
    { name: 'Home', href: '/' },
    { name: 'News', href: '/news' },
    { name: 'Leaderboard', href: `/leaderboard/${Sites.STAKE}` },
    { name: 'Store', href: '/store' },
    { name: 'SokoPlay', href: '/sokoplay' },
];

const COMMUNITY_LINKS = [
    { name: 'Challenges', href: '/challenges' },
    { name: 'Bonuses', href: '/bonuses' },
    { name: 'Raffles', href: '/raffles' },
    { name: 'Earn', href: '/clips' },
    { name: 'Terms & Conditions', href: '/terms' },
    { name: 'Privacy Policy', href: '/privacy' },
];

export default function Footer() {
    const pathname = usePathname();
    if (pathname?.startsWith('/admin') || pathname?.startsWith('/raffle-overlay')) return null;

    return (
        <footer className="pt-16 pb-8 px-4 text-gray-300 w-full z-50 backdrop-blur shadow-sm border-t border-green-200/20">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 pb-12">

                    {/* Brand */}
                    <div className="sm:col-span-2 lg:col-span-1">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="relative w-10 h-10 rounded-full overflow-hidden shrink-0">
                                <Image src="/img/logo.webp" alt="sokoloco" fill className="object-cover" />
                            </div>
                            <span className="text-xl font-mono font-bold tracking-widest bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                                SOKOLOCO
                            </span>
                        </div>
                        <p className="text-sm text-gray-400 leading-relaxed mb-5 max-w-xs">
                            Kick casino streamer building a community-driven rewards platform — leaderboards, points, and SokoPlay games for the crew.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {socials.map((social, index) => {
                                const Icon = social.icon;
                                return (
                                    <Link
                                        key={index}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={social.name}
                                        title={social.name}
                                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 transition-all duration-200 hover:bg-white/5 hover:-translate-y-0.5 ${social.color}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-green-400 mb-4">
                            Quick Links
                        </h3>
                        <ul className="space-y-2.5 text-sm">
                            {QUICK_LINKS.map((link) => (
                                <li key={link.href}>
                                    <Link href={link.href} className="text-gray-400 hover:text-green-400 transition-colors duration-200">
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Community */}
                    <div>
                        <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-green-400 mb-4">
                            Community
                        </h3>
                        <ul className="space-y-2.5 text-sm">
                            {COMMUNITY_LINKS.map((link) => (
                                <li key={link.href}>
                                    <Link href={link.href} className="text-gray-400 hover:text-green-400 transition-colors duration-200">
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact & Responsible Gambling */}
                    <div>
                        <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-green-400 mb-4">
                            Get In Touch
                        </h3>
                        <p className="text-sm text-gray-400 mb-1">Business Inquiries</p>
                        <Link
                            href="mailto:sokolovasrimtautasmatas@gmail.com"
                            className="text-sm text-green-400 hover:text-green-300 underline underline-offset-2 transition-colors duration-200 break-all"
                        >
                            sokolovasrimtautasmatas@gmail.com
                        </Link>

                        <Link href="https://www.gambleaware.org/" target="_blank" rel="noopener noreferrer" className="inline-block mt-6">
                            <Image
                                src="/img/begambleaware.png"
                                alt="BeGambleAware"
                                width={160}
                                height={21}
                                className="w-40 h-auto opacity-60 hover:opacity-100 transition-opacity duration-300"
                            />
                        </Link>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-green-400/10 pt-6 text-center space-y-3">
                    <p className="text-xs text-gray-500 leading-relaxed max-w-3xl mx-auto">
                        {'>'} Gambling involves risk. Only gamble with funds you can afford to lose. We do not take responsibility for any possible losses while using any platforms promoted on this website. {'<'}
                    </p>
                    <p className="text-xs font-mono text-green-400/50">
                        © 2025 SOKOLOCO • ALL RIGHTS RESERVED • GAMBLE RESPONSIBLY
                    </p>
                </div>
            </div>
        </footer>
    );
}
